import { motion } from "framer-motion";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";

const images = [
  {
    url: "/gorilla.webp",
    title: "Gorilla Trekking",
    description: "Set Out on a vibrant Gorilla Trekking Adventure in Bwindi Impenetrable National Park"
  },
  {
    url: "https://images.unsplash.com/photo-1523805009345-7448845a9e53",
    title: "Kidepo Valley",
    description: "Explore the Unspoiled Wilderness of Kidepo Valley National Park"
  },
  {
    url: "https://images.unsplash.com/photo-1671360788724-100050645e10",
    title: "Murchison Falls",
    description: "Witness the Nile River's most breathtaking moment as it thunders through a 7-meter gorge"
  },
  {
    url: "/5O5A9186.webp",
    title: "Experience Nature",
    description: "19-Day Journey through Uganda's Stunning Landscapes and Rare Wildlife"
  },
  {
    url: "/crested-crane.webp",
    title: "Cultural and Wildlife Safari",
    description: "A 21-Day Journey Through Uganda's Stunning Landscapes, Incredible Wildlife, and Rich Cultural Heritage"
  },
  {
    url: "/chimpanzee.webp",
    title: "Chimpanzee Tracking",
    description: "Track chimpanzees in Kibale Forest National Park"
  }
];

export default function ImageGrid() {
  return (
    <div className="relative py-16 sm:py-20 lg:py-24 overflow-visible">
      <div 
        className="absolute inset-0 bg-white pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='black' fill-opacity='0.08'/%3E%3C/svg%3E")`,
          zIndex: -1,
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-12"
        >
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Explore Uganda</h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Discover the diverse experiences waiting for you in Uganda
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {images.map((image, index) => (
            <motion.div
              key={image.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="relative group aspect-[4/3] overflow-hidden rounded-xl touch-manipulation"
            >
              <img
                src={image.url}
                alt={image.title}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/20 group-hover:bg-black/50 transition-colors duration-300" />

              <div className="absolute inset-0 flex flex-col items-center justify-center p-4 sm:p-6 text-white">
                <h3 className="text-xl sm:text-2xl font-bold mb-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                  {image.title}
                </h3>
                <p className="text-sm sm:text-base text-center opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300 delay-100">
                  {image.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-12 relative z-20">
          <a 
            href="/explore-uganda" 
            className="inline-block bg-primary hover:bg-primary/90 text-white px-8 py-2 rounded-full shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer font-medium"
          >
            Explore Uganda
          </a>
        </div>
      </div>
    </div>
  );
}